import {
  STORAGE_URL,
  TICKET_ATTACHMENTS_DIR,
  TICKET_ATTACHMENT_EXTENSIONS,
  TICKET_ATTACHMENT_MAX_BYTES,
  TICKET_ATTACHMENT_MAX_FILES,
} from '@/utils/const';
import { UserRoles } from '@/utils/enums';

/**
 * Support tickets: what the ticket components share — which side of a
 * conversation a message is on, the files sent with it, and how a message's
 * time reads. The same module lives in the Screenkode frontend.
 */

export { avatarColorClass, initialsOf, timeAgo } from '@/utils/display';

/** The two sides of a conversation, as the API names them (its utils/ticket). */
export const TICKET_SIDE_STAFF = 'staff';
export const TICKET_SIDE_ORGANIZATION = 'organization';

/**
 * What every ticket request (services/ticket, services/ticket_comment) tells
 * the API about who is asking. The admin asks as itself: a super admin here is
 * Screenkode staff. The organization apps ask as the organization — their own
 * utils/ticket.
 */
export const TICKET_REQUEST_PARAMS: Readonly<Record<string, string>> = {};

/** The side the admin is on, where the API has not said (its `viewerSide`): staff for a super admin. */
export function viewerTicketSide(account?: TicketPersonLike | null): string {
  return account?.role === UserRoles.superAdmin ? TICKET_SIDE_STAFF : TICKET_SIDE_ORGANIZATION;
}

export interface TicketAttachmentLike {
  name?: string | null;
  key?: string | null;
  url?: string | null;
  size?: number | null;
  mimetype?: string | null;
}

export interface TicketPersonLike {
  id?: string | null;
  role?: string | null;
  fname?: string | null;
  lname?: string | null;
  email?: string | null;
}

export interface TicketCommentLike {
  authorSide?: string | null;
  user?: TicketPersonLike | null;
}

/* ------------------------------- uploads --------------------------------- */

export const TICKET_ATTACHMENT_ACCEPT = TICKET_ATTACHMENT_EXTENSIONS.join(',');
export const TICKET_ATTACHMENT_MAX_MB = Math.round(TICKET_ATTACHMENT_MAX_BYTES / (1024 * 1024));

/**
 * Axios options for a request that carries files: the browser sets the
 * multipart boundary, and a large upload gets the time it needs.
 */
export const UPLOAD_REQUEST_CONFIG = {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 5 * 60 * 1000,
};

/** The multipart body a ticket or a message is sent as: its fields, then each file under `attachments`. */
export function ticketFormData(fields: object, attachments?: File[] | null): FormData {
  const form = new FormData();
  Object.entries(fields as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    form.append(key, String(value));
  });
  (attachments || []).forEach((file) => form.append('attachments', file, file.name));
  return form;
}

export function fileExtension(name?: string | null): string {
  const match = /\.[^./\\]+$/.exec(String(name || ''));
  return match ? match[0].toLowerCase() : '';
}

export function formatFileSize(bytes?: number | null): string {
  const size = Number(bytes) || 0;
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** Why a picked file cannot be sent, or null when it can. Mirrors the API's upload rules. */
export function attachmentProblem(file: File): string | null {
  if (!TICKET_ATTACHMENT_EXTENSIONS.includes(fileExtension(file.name))) {
    return `"${file.name}" is not a file type that can be attached. Images, PDFs, documents, spreadsheets, text, zip and short videos are accepted.`;
  }
  if (file.size > TICKET_ATTACHMENT_MAX_BYTES) {
    return `"${file.name}" is larger than ${TICKET_ATTACHMENT_MAX_MB}MB.`;
  }
  if (!file.size) {
    return `"${file.name}" is empty.`;
  }
  return null;
}

/** The files to keep after a pick: each once, each acceptable, within the limit — and why any were left out. */
export function acceptAttachments(
  current: File[],
  picked: ArrayLike<File> | null | undefined
): { files: File[]; errors: string[] } {
  const files = [...current];
  const errors: string[] = [];

  Array.from(picked || []).forEach((file) => {
    if (files.some((kept) => kept.name === file.name && kept.size === file.size && kept.lastModified === file.lastModified)) return;

    const problem = attachmentProblem(file);
    if (problem) {
      errors.push(problem);
      return;
    }
    if (files.length >= TICKET_ATTACHMENT_MAX_FILES) {
      if (!errors.includes(TOO_MANY_FILES)) errors.push(TOO_MANY_FILES);
      return;
    }
    files.push(file);
  });

  return { files, errors };
}

const TOO_MANY_FILES = `Up to ${TICKET_ATTACHMENT_MAX_FILES} files can be sent with one message.`;

/* ------------------------------ attachments ------------------------------ */

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

export function isImageFile(name?: string | null, type?: string | null): boolean {
  return /^image\//i.test(String(type || '')) || IMAGE_EXTENSIONS.includes(fileExtension(name));
}

/** Shown as a picture rather than a file: an image the browser can draw. */
export function isImageAttachment(attachment: TicketAttachmentLike): boolean {
  return isImageFile(attachment.name, attachment.mimetype) && !/\.heic$/i.test(String(attachment.name || ''));
}

/**
 * Where an attachment is served from. A row without a full address (a file
 * over 5MB was once stored with no https://) is found by its storage key.
 */
export function attachmentUrl(attachment: TicketAttachmentLike): string {
  if (/^https?:\/\//i.test(String(attachment.url || ''))) return String(attachment.url);
  const key = String(attachment.key || '');
  if (!key) return '';
  const file = key.split('/').pop() || key;
  return `${STORAGE_URL}/${TICKET_ATTACHMENTS_DIR}/${file}`;
}

/** The primeicons glyph for a file of this kind. */
export function attachmentIcon(attachment: TicketAttachmentLike | File): string {
  const name = String(attachment.name || '');
  const type = String((attachment as TicketAttachmentLike).mimetype || (attachment as File).type || '');
  const extension = fileExtension(name);
  if (isImageFile(name, type)) return 'pi-image';
  if (extension === '.pdf') return 'pi-file-pdf';
  if (['.xls', '.xlsx', '.csv'].includes(extension)) return 'pi-file-excel';
  if (['.doc', '.docx', '.rtf'].includes(extension)) return 'pi-file-word';
  if (['.mp4', '.mov', '.webm'].includes(extension) || /^video\//.test(type)) return 'pi-video';
  if (extension === '.zip') return 'pi-folder';
  return 'pi-file';
}

/* -------------------------------- people --------------------------------- */

/** Which side wrote a message: as the API says, else from the author's role. */
export function ticketSideOf(comment: TicketCommentLike): string {
  if (comment.authorSide === TICKET_SIDE_STAFF || comment.authorSide === TICKET_SIDE_ORGANIZATION) return comment.authorSide;
  return comment.user?.role === UserRoles.superAdmin ? TICKET_SIDE_STAFF : TICKET_SIDE_ORGANIZATION;
}

function capitalize(value?: string | null): string {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/** "Beth Israel" — the person's name, else their email, else the fallback. */
export function personName(user?: TicketPersonLike | null, fallback = 'Requester'): string {
  const name = [capitalize(user?.fname), capitalize(user?.lname)].filter(Boolean).join(' ');
  return name || String(user?.email || '').trim() || fallback;
}

/* --------------------------------- time ---------------------------------- */

export function dateOf(value?: string | number | Date | null): Date | null {
  if (value == null || value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function daysAgo(date: Date, now: Date): number {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOf(now) - startOf(date)) / 86400000);
}

/** "3:12 PM" today, "Yesterday, 3:12 PM", "Mon, Sep 29, 3:12 PM" this year, else with the year. */
export function messageTimeLabel(value?: string | number | Date | null): string {
  const date = dateOf(value);
  if (!date) return '';
  const now = new Date();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const days = daysAgo(date, now);
  if (days === 0) return time;
  if (days === 1) return `Yesterday, ${time}`;
  const sameYear = date.getFullYear() === now.getFullYear();
  const day = date.toLocaleDateString('en-US', sameYear
    ? { weekday: 'short', month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
  return `${day}, ${time}`;
}

/** The separator between two days of a conversation: "Today", "Yesterday", "Monday, September 29", … */
export function messageDayLabel(value?: string | number | Date | null): string {
  const date = dateOf(value);
  if (!date) return '';
  const now = new Date();
  const days = daysAgo(date, now);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-US', sameYear
    ? { weekday: 'long', month: 'long', day: 'numeric' }
    : { month: 'long', day: 'numeric', year: 'numeric' });
}

interface TicketCopyLike {
  lastModifiedAt?: string | Date | null;
}

/**
 * Whether one copy of a ticket was described before the other was last
 * changed: a reload that left before a rename comes back with the ticket as
 * it was. A copy without the time is never the older.
 */
export function isOlderCopy(ticket?: TicketCopyLike | null, than?: TicketCopyLike | null): boolean {
  const at = dateOf(ticket?.lastModifiedAt);
  const thanAt = dateOf(than?.lastModifiedAt);
  return Boolean(at && thanAt && at.getTime() < thanAt.getTime());
}
